// Copyright 2025, University of Maryland, All Rights Reserved
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from "@/components/ui/card"
import { Switch } from "@/components/ui/switch"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Label } from "@/components/ui/label"
import { Rocket, Database } from "lucide-react"
import { useState, useEffect } from "react"
import { getAllAPISessions, apiToDisplaySession, type APISession } from "@/types/session"
import type { ConfigComponentProps } from '../ConfigForm'
import type { DataSource } from '@/api/types/config'

export function ModeSettings({ config, onUpdate, isLoading }: ConfigComponentProps) {
  const [monitoringSessions, setMonitoringSessions] = useState<{ [sessionCode: string]: boolean }>(
    config?.monitoring_sessions || {}
  )
  // Store the actual value from DynamoDB, which might not be a valid DataSource type
  // Convert empty string to null for proper handling
  const [dataSource, setDataSource] = useState<string | null>(
    (config?.data_source && config.data_source.trim() !== '') ? config.data_source : null
  )

  const availableSessions = getAllAPISessions()


  // Update local state when config changes (workspace switch)
  useEffect(() => {
    setMonitoringSessions(config?.monitoring_sessions || {})
    const newDataSource = (config?.data_source && config.data_source.trim() !== '') ? config.data_source : null
    console.log('[ModeSettings] Setting dataSource:', newDataSource, 'from config.data_source:', config?.data_source)
    setDataSource(newDataSource)
  }, [config?.monitoring_sessions, config?.data_source])
  
  const handleSessionToggle = (sessionCode: APISession, enabled: boolean) => {
    const updatedSessions = {
      ...monitoringSessions,
      [sessionCode]: enabled
    }
    setMonitoringSessions(updatedSessions)

    // Only update parent when user actually changes something
    onUpdate({
      monitoring_sessions: updatedSessions
    })
  }

  const handleDataSourceChange = (value: DataSource) => {
    setDataSource(value)
    onUpdate({
      data_source: value
    })
  }

  return (
    <div className="space-y-4">
      {/* Data Source Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5 text-blue-500"/>
            Data Source
          </CardTitle>
          <CardDescription>
            Select the primary legislative data source for this workspace
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label htmlFor="data-source-select">Data Source</Label>
            {dataSource ? (
              <Select
                value={dataSource}
                onValueChange={handleDataSourceChange}
                disabled={isLoading}
              >
                <SelectTrigger id="data-source-select" className="w-64">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {/* Show current value if it's not Maryland */}
                  {dataSource !== 'Maryland' && (
                    <SelectItem value={dataSource}>{dataSource}</SelectItem>
                  )}
                  <SelectItem value="Maryland">Maryland</SelectItem>
                </SelectContent>
              </Select>
            ) : (
              <div className="w-64 px-3 py-2 border border-gray-200 rounded-md bg-gray-50 text-gray-500 text-sm">
                Loading...
              </div>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Bill Monitoring Configuration */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Rocket className="h-5 w-5 text-blue-500"/>
            Bill Monitoring
          </CardTitle>
          <CardDescription>
            Enable the system to automatically evaluate bills for monitoring by session
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-4">
            <div className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Session Monitoring Configuration
            </div>
            {availableSessions.map((sessionCode) => (
              <div key={sessionCode} className="flex items-center space-x-2">
                <Switch
                  id={`monitoring-switch-${sessionCode}`}
                  checked={monitoringSessions[sessionCode] || false}
                  onCheckedChange={(checked) => handleSessionToggle(sessionCode, checked)}
                  disabled={isLoading}
                  aria-labelledby={`monitoring-switch-label-${sessionCode}`}
                />
                <label
                  htmlFor={`monitoring-switch-${sessionCode}`}
                  id={`monitoring-switch-label-${sessionCode}`}
                  className="text-sm font-medium leading-none cursor-pointer"
                >
                  {apiToDisplaySession(sessionCode)}
                </label>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  )
}